import React, { useEffect, useState, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider } from './shared/contexts/AuthContext';
import { ModalProvider } from './shared/contexts/ModalContext';
import { PlanProvider } from './shared/contexts/PlanContext';
import { NoticePostsProvider } from './shared/contexts/NoticePostsContext';

// Teachering 스타일 시스템 import
import './styles/teachering/index.css';
// WhatQuiz 전체 변수 시스템 import
import './styles/variables.css';

// Firebase 핵심 기능만 import - 나머지는 지연 로딩
import { handleGoogleRedirectResult } from './firebase-core';
import { useAuth } from './shared/contexts/AuthContext';
import { useModal } from './shared/contexts/ModalContext'; // useModal import 추가
import LoginModal from './components/teachering/components/LoginModal'; // LoginModal import 추가
import RegisterModal from './components/teachering/components/RegisterModal'; // RegisterModal import 추가

// Components
import ScrollToTop from './components/common/ScrollToTop';
import ProtectedRoute from './components/common/ProtectedRoute';
import ShortFormQuizHomePage from './pages/shortFormQuiz/ShortFormQuizHomePage';
import ShortFormQuizSettingPage from './pages/shortFormQuiz/ShortFormQuizSettingPage';
import ShortFormQuizTeacherPage from './pages/shortFormQuiz/ShortFormQuizTeacherPage';
import ShortFormQuizModifyPage from './pages/shortFormQuiz/ShortFormQuizModifyPage';
import ShortFormQuizStudentPage from './pages/shortFormQuiz/ShortFormQuizStudentPage';
import MainHeader from './components/teachering/layout/MainHeader.js';
import MainFooter from './components/teachering/layout/MainFooter.js';
import SayingPaperPage from './pages/dailySaying/SayingPaperPage';
import AccountDeletionOverlay from './components/common/AccountDeletionOverlay';

// Pages (동적 import)
const TeacheringHomePage = React.lazy(() => import('./pages/teachering/HomePage.js'));
const HelpCenterPage = React.lazy(() => import('./pages/teachering/HelpCenter/HelpCenterPage.js'));
const ProfilePage = React.lazy(() => import('./pages/teachering/ProfilePage.js'));
const TermsOfServicePage = React.lazy(() => import('./pages/teachering/HelpCenter/TermsOfServicePage.js'));
const PrivacyPolicyPage = React.lazy(() => import('./pages/teachering/HelpCenter/PrivacyPolicyPage.js'));
const NoticePage = React.lazy(() => import('./pages/teachering/HelpCenter/NoticePage.js'));
const FAQ = React.lazy(() => import('./pages/teachering/HelpCenter/FAQ.js'));
const UpdatesPage = React.lazy(() => import('./pages/teachering/HelpCenter/UpdatesPage.js'));
const EventsPage = React.lazy(() => import('./pages/teachering/HelpCenter/EventsPage.js'));
const PostDetailPage = React.lazy(() => import('./pages/teachering/HelpCenter/PostDetailPage.js'));
const PricingPage = React.lazy(() => import('./pages/teachering/Pricing/Pricing.js'));
const SubscribeCheckoutPage = React.lazy(() => import('./pages/teachering/Pricing/SubscribeCheckoutPage.js'));
const SubscribeSuccessPage = React.lazy(() => import('./pages/teachering/Pricing/SubscribeSuccessPage.js'));
const SubscribeFailPage = React.lazy(() => import('./pages/teachering/Pricing/SubscribeFailPage.js'));
const RefundPolicyPage = React.lazy(() => import('./pages/teachering/Pricing/RefundPolicyPage.js'));
const RolePickerHomePage = React.lazy(() => import('./pages/rolePicker/RolePickerHomePage'));
const RolePickerTeacherView = React.lazy(() => import('./pages/rolePicker/RolePickerTeacherView'));
const RolePickerStudentView = React.lazy(() => import('./pages/rolePicker/RolePickerStudentView'));
const RolePickerPrintPage = React.lazy(() => import('./pages/rolePicker/RolePickerPrintPage'));
const RolePickerGamePage = React.lazy(() => import('./pages/rolePicker/RolePickerGamePage'));
const StudyGroupPage = React.lazy(() => import('./pages/studyGroup/StudyGroupPage'));

const StudyGroupStudentPage = React.lazy(() => import('./pages/studyGroup/StudyGroupStudentPage'));
const StudyGroupHomePage = React.lazy(() => import('./pages/studyGroup/StudyGroupHomePage'));
const StudyGroupPrintPage = React.lazy(() => import('./pages/studyGroup/StudyGroupPrintPage'));
const SurveyQuestion = React.lazy(() => import('./pages/studyGroup/SurveyQuestion'));
const SurveyResult = React.lazy(() => import('./pages/studyGroup/SurveyResult'));


const TeacherTestHomePage = React.lazy(() => import('./pages/teacherTest/TeacherTestHomePage'));
const TeacherTestPaperPage = React.lazy(() => import('./pages/teacherTest/TeacherTestPaperPage'));
const TeacherTestStudentPaperPage = React.lazy(() => import('./pages/teacherTest/TeacherTestStudentPaperPage'));
const TeacherTestPreviewPage = React.lazy(() => import('./pages/teacherTest/TeacherTestPreviewPage'));
const TeacherTestDashboard = React.lazy(() => import('./pages/teacherTest/TeacherTestDashboard'));
const ShortUrlRedirect = React.lazy(() => import('./pages/teacherTest/ShortUrlRedirect'));
const TestResultPage = React.lazy(() => import('./pages/teacherTest/TestResultPage'));
const DailySayingPage = React.lazy(() => import('./pages/dailySaying/DailySayingPage'));
const SayingsUploadPage = React.lazy(() => import('./pages/dailySaying/SayingsUploadPage'));
const ExcelDebugger = React.lazy(() => import('./pages/dailySaying/ExcelDebugger'));
const MarbleDrawingHomePage = React.lazy(() => import('./pages/marbleDrawing/MarbleDrawingHomePage'));
const MarbleDrawingClassSettingPage = React.lazy(() => import('./pages/marbleDrawing/MarbleDrawingClassSettingPage'));
const MarbleDrawingSettingPage = React.lazy(() => import('./pages/marbleDrawing/MarbleDrawingSettingPage'));
const MarbleGamePage = React.lazy(() => import('./pages/marbleDrawing/MarbleGamePage'));

const DebateBattlePage = React.lazy(() => import('./pages/debateBattle/DebateBattlePage'));
const DebateBattleEnglishPage = React.lazy(() => import('./pages/debateBattle/DebateBattleEnglishPage'));
const DebateBattleStudentPage = React.lazy(() => import('./pages/debateBattle/DebateBattleStudentPage'));
const ContentsPage = React.lazy(() => import('./pages/teachering/ContentsPage.js'));
const ContentDetailPage = React.lazy(() => import('./pages/teachering/ContentDetailPage.js'));
const ClassManagementPage = React.lazy(() => import('./pages/teachering/ClassManagement/ClassManagementPage.js'));
const ClassDetailPage = React.lazy(() => import('./pages/teachering/ClassManagement/ClassDetailPage.js'));
const ActivityDetailPage = React.lazy(() => import('./pages/teachering/ClassManagement/ActivityDetailPage'));
const ActivityHistoryPage = React.lazy(() => import('./pages/teachering/ClassManagement/ActivityHistoryPage'));
const StudentDetailPage = React.lazy(() => import('./pages/teachering/ClassManagement/StudentDetailPage'));
const SchoolRecordHistoryPage = React.lazy(() => import('./pages/teachering/ClassManagement/SchoolRecordHistoryPage'));
const SchoolRecordDetailPage = React.lazy(() => import('./pages/teachering/ClassManagement/SchoolRecordDetailPage'));
const MindMapSoloPage = React.lazy(() => import('./pages/mindMap/MindMapSoloPage'));
const MindMapTogetherPage = React.lazy(() => import('./pages/mindMap/MindMapTogetherPage'));
const MindMapStudentSoloPage = React.lazy(() => import('./pages/mindMap/MindMapStudentSoloPage'));
const MindMapStudentTogetherPage = React.lazy(() => import('./pages/mindMap/MindMapStudentTogetherPage'));
const AdminSettingMainPage = React.lazy(() => import('./pages/teachering/AdminSetting/AdminSettingMainPage'));
const AdminSettingUserAccount = React.lazy(() => import('./pages/teachering/AdminSetting/AdminSettingUserAccount'));
const AdminSettingCoupon = React.lazy(() => import('./pages/teachering/AdminSetting/AdminSettingCoupon'));
const AdminSettingNoticeBoard = React.lazy(() => import('./pages/teachering/AdminSetting/AdminSettingNoticeBoard'));
const AdminSettingMainNotice = React.lazy(() => import('./pages/teachering/AdminSetting/AdminSettingMainNotice'));
const SchoolRecordMainPage = React.lazy(() => import('./pages/teachering/SchoolRecord/SchoolRecordMainPage'));

function App() {
  const [currentUrl, setCurrentUrl] = useState('');
  
  // 동적으로 파비콘 변경하기
  useEffect(() => {
    // 파비콘 링크 엘리먼트 만들기
    const link = document.querySelector("link[rel*='icon']") || document.createElement('link');
    link.type = 'image/x-icon';
    link.rel = 'shortcut icon';
    link.href = `${process.env.PUBLIC_URL}/assets/icons/favicon.ico`;  // 정적 파비콘 사용
    document.getElementsByTagName('head')[0].appendChild(link);
  }, []);

  return (
    <HelmetProvider>
      <AuthProvider>
        <NoticePostsProvider>
          <PlanProvider>
            <ModalProvider>
              <Router
                future={{
                  v7_startTransition: true,
                  v7_relativeSplatPath: true
                }}
              >
                <ScrollToTop />
                <AccountDeletionOverlay />
                <AppLayout currentUrl={currentUrl} setCurrentUrl={setCurrentUrl} />
              </Router>
            </ModalProvider>
          </PlanProvider>
        </NoticePostsProvider>
      </AuthProvider>
    </HelmetProvider>
  );
}

// Layout 컴포넌트: location에 따라 헤더/푸터 숨김 처리
function AppLayout({ currentUrl, setCurrentUrl }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser, deletionState } = useAuth();
  const { showLoginModal, closeLoginModal, openLoginModal, showRegisterModal, closeRegisterModal, openRegisterModal } = useModal();
  const prevDeletionStatus = usePrevious(deletionState.status);

  useEffect(() => {
    // 삭제 성공 후 오버레이가 사라지는 시점에 홈으로 이동
    if (prevDeletionStatus === 'success' && deletionState.status === 'inactive') {
      navigate('/');
    }
  }, [deletionState.status, prevDeletionStatus, navigate]);

  // 이전 상태 저장을 위한 커스텀 훅
  function usePrevious(value) {
    const ref = React.useRef();
    React.useEffect(() => {
      ref.current = value;
    });
    return ref.current;
  }
  
  // 브라우저 탭 제목을 항상 고정 - 강력한 전역 관리
  useEffect(() => {
    const FIXED_TITLE = '티처링 - AI 교육도구 플랫폼';
    
    // 초기 제목 설정
    document.title = FIXED_TITLE;
    
    // MutationObserver를 사용해서 title 변경을 실시간 감지하고 즉시 복원
    const titleObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList' || mutation.type === 'characterData') {
          const currentTitle = document.title;
          if (currentTitle !== FIXED_TITLE) {
            // 제목이 변경되었다면 즉시 원래 제목으로 복원
            document.title = FIXED_TITLE;
          }
        }
      });
    });
    
    // title 태그와 head 태그를 모두 관찰
    const titleElement = document.querySelector('title');
    const headElement = document.querySelector('head');
    
    if (titleElement) {
      titleObserver.observe(titleElement, {
        childList: true,
        characterData: true,
        subtree: true
      });
    }
    
    if (headElement) {
      titleObserver.observe(headElement, {
        childList: true,
        subtree: true
      });
    }
    
    // 추가적으로 일정 간격으로 제목 체크 (Helmet 등의 비동기 변경 대응)
    const titleCheckInterval = setInterval(() => {
      if (document.title !== FIXED_TITLE) {
        document.title = FIXED_TITLE;
      }
    }, 100); // 100ms마다 체크
    
    // cleanup
    return () => {
      titleObserver.disconnect();
      clearInterval(titleCheckInterval);
    };
  }, []); // 컴포넌트 마운트 시 한 번만 설정

  // Google Redirect 결과 처리 (페이지 로드 시)
  useEffect(() => {
    const handleRedirectResult = async () => {
      try {
        const result = await handleGoogleRedirectResult();
        if (result.isRedirectResult && result.user) {
          console.log('Google Redirect 로그인 완료:', result.user.email);
          // 필요한 경우 추가 처리 (예: 페이지 리다이렉트, 알림 등)
        }
      } catch (error) {
        console.error('Google Redirect 결과 처리 오류:', error);
      }
    };

    handleRedirectResult();
  }, []); // 컴포넌트 마운트 시 한 번만 실행

  // 채널톡 제어 (지정된 페이지들에서만 보이게)
  useEffect(() => {
    const channelTalkVisiblePages = [
      '/',                    // 메인 홈페이지
      '/contents',          // 전체보기
      '/class-management',   // 학급관리
      '/help-center',        // 지원센터
      '/privacy-policy',      // 개인정보처리방침
      '/terms-of-service',    // 이용약관
      '/notice',              // 공지사항 메인
      '/faq'                  // 자주 묻는 질문 메인
    ];
    
    // 공지사항과 FAQ의 하위 페이지도 포함 (예: /notice/1, /faq/2)
    const isChannelTalkVisiblePage = channelTalkVisiblePages.includes(location.pathname) ||
      location.pathname.startsWith('/notice/') ||
      location.pathname.startsWith('/faq/');
    
    const hideChannelTalk = () => {
      const style = document.createElement('style');
      style.id = 'hide-channel-talk-global';
      style.innerHTML = `
        .channel-plugin-ch-plugin-core,
        #ch-plugin,
        #ch-plugin-script + div,
        div[class*="channel"],
        iframe[src*="channel.io"],
        .channel-plugin-launcher,
        .ch-plugin-launcher,
        .ch-plugin-core,
        [data-ch-plugin] {
          display: none !important;
          visibility: hidden !important;
          opacity: 0 !important;
          pointer-events: none !important;
        }
        
        body > div[id*="ch-"],
        body > div[class*="ch-"],
        body > div[class*="channel"] {
          display: none !important;
          visibility: hidden !important;
        }
      `;
      document.head.appendChild(style);
    };

    const showChannelTalk = () => {
      const style = document.getElementById('hide-channel-talk-global');
      if (style) {
        style.remove();
      }
    };

    const hideChannelTalkElements = () => {
      const selectors = [
        '#ch-plugin',
        '.channel-plugin-ch-plugin-core',
        '.ch-plugin-launcher',
        '.channel-plugin-launcher',
        'iframe[src*="channel.io"]',
        '[data-ch-plugin]',
        'div[id*="ch-"]',
        'div[class*="channel"]'
      ];

      selectors.forEach(selector => {
        const elements = document.querySelectorAll(selector);
        elements.forEach(element => {
          if (isChannelTalkVisiblePage) {
            // 허용된 페이지에서는 보이게
            element.style.display = '';
            element.style.visibility = '';
            element.style.opacity = '';
            element.style.pointerEvents = '';
          } else {
            // 다른 페이지에서는 숨기기
            element.style.display = 'none';
            element.style.visibility = 'hidden';
            element.style.opacity = '0';
            element.style.pointerEvents = 'none';
          }
        });
      });
    };

    if (isChannelTalkVisiblePage) {
      // 허용된 페이지에서는 채널톡 보이기
      showChannelTalk();
      setTimeout(() => hideChannelTalkElements(), 100); // 채널톡 로드 후 실행
    } else {
      // 다른 페이지에서는 채널톡 숨기기
      hideChannelTalk();
      hideChannelTalkElements();
    }

    // 주기적으로 채널톡 요소 체크 (새로 생성되는 경우 대응)
    const interval = setInterval(() => {
      hideChannelTalkElements();
    }, 1000);

    return () => {
      clearInterval(interval);
    };
  }, [location.pathname]);
  
  const isStudentPage = location.pathname.includes('/student');  // 학생용 페이지 여부 확인
  const isQuestionPage = location.pathname.includes('/question/');  // 학습유형 질문 페이지 여부 확인
  const isTermsPage = location.pathname === '/terms-of-service';  // 이용약관 페이지 여부 확인
  const isPrivacyPage = location.pathname === '/privacy-policy';  // 개인정보처리방침 페이지 여부 확인
  const isPrintPage = location.pathname.includes('/print');  // 프린트 페이지 여부 확인
  const isMarbleGamePage = location.pathname === '/play/marble-game';  // 구슬 게임 페이지 여부 확인
  
  // 모바일 가로 모드 감지
  const [windowWidth, setWindowWidth] = React.useState(window.innerWidth);
  const [windowHeight, setWindowHeight] = React.useState(window.innerHeight);
  const isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  const isMobileSize = Math.min(windowWidth, windowHeight) <= 768;
  const isMobile = isMobileDevice || isMobileSize;
  const isLandscapeMode = windowWidth > windowHeight;
  
  // 화면 크기 변화 감지 (가로 모드 헤더/푸터 제어용)
  React.useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
      setWindowHeight(window.innerHeight);
    };

    const handleOrientationChange = () => {
      setTimeout(() => {
        handleResize();
      }, 100);
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleOrientationChange);
    
    const screenOrientation = window.screen?.orientation;
    if (screenOrientation?.addEventListener) {
      screenOrientation.addEventListener('change', handleOrientationChange);
    }

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleOrientationChange);
      
      if (screenOrientation?.removeEventListener) {
        screenOrientation.removeEventListener('change', handleOrientationChange);
      }
    };
  }, []);
  
  // 헤더와 푸터를 숨길 페이지들 (조건별로 다르게 처리)
  const hideHeaderFooterPages = 
    (isPrintPage) || 
    (isMarbleGamePage && isMobile && isLandscapeMode) ||  // 구슬 게임: 모바일 가로모드에서만 숨김
    location.pathname.includes('/play/daily-saying') ||  // dailySaying 폴더의 모든 페이지
    location.pathname.includes('/play/debate-battle') ||  // debateBattle 폴더의 모든 페이지
    location.pathname.includes('/play/debate-battle-english') ||  // debateBattle English 폴더의 모든 페이지
    location.pathname.includes('/play/marble-drawing') ||  // marbleDrawing 폴더의 모든 페이지
    location.pathname.includes('/play/marble-game') ||  // MarbleGamePage
    location.pathname.includes('/play/role-picker') ||  // rolePicker 폴더의 모든 페이지
    location.pathname.includes('/play/shortFormQuiz') ||  // shortFormQuiz 폴더의 모든 페이지
    location.pathname.includes('/play/study-group') ||  // studyGroup 폴더의 모든 페이지
    location.pathname.includes('/play/teacher-test') ||  // teacherTest 폴더의 모든 페이지
    location.pathname.includes('/play/mind-map-solo') || // 마인드맵 페이지
    location.pathname.includes('/play/mind-map-together') || // 다함께 마인드맵 페이지
    location.pathname === '/play/test-result' ||  // TestResultPage
    location.pathname.includes('/play/saying-paper');  // SayingPaperPage

  useEffect(() => {
    // EDU GPT(Deep Edu AI) 외부 주소 패턴을 모두 포함!
    const isEduGpt = currentUrl && (
      currentUrl.includes('quiz-ai') ||
      currentUrl.includes('feedbackAki') ||
      currentUrl.includes('chat') ||
      currentUrl.includes('deepedu')
    );

    if (isEduGpt) {
      document.body.setAttribute('data-page', 'deepedu');
    } else if (isStudentPage || isQuestionPage) {
      document.body.setAttribute('data-page', 'student');
    } else {
      document.body.removeAttribute('data-page');
    }
    return () => {
      document.body.removeAttribute('data-page');
    };
  }, [currentUrl, isStudentPage, isQuestionPage]);

  return (
    <div>
      {!hideHeaderFooterPages && (
        <MainHeader />
      )}
      <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100dvh' }}>
        <Suspense fallback={<div>로딩 중...</div>}>
          <Routes>
            <Route path="/" element={<TeacheringHomePage />} />
            <Route path="/help-center" element={<HelpCenterPage />} />
            <Route path="/contents" element={<ContentsPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/pricing/subscribe" element={<SubscribeCheckoutPage />} />
            <Route path="/pricing/subscribe/success" element={<SubscribeSuccessPage />} />
            <Route path="/pricing/subscribe/fail" element={<SubscribeFailPage />} />
            <Route path="/pricing/refund-policy" element={<RefundPolicyPage />} />
            {/* 올바른 경로만 제공 */}
            <Route path="/play/shortFormQuiz" element={<ShortFormQuizHomePage />} />
            <Route path="/play/shortFormQuiz/setting" element={<ShortFormQuizSettingPage />} />
            {/* code(=id) 기반 라우트 */}
            <Route path="/play/shortFormQuiz/:code/teacher" element={<ShortFormQuizTeacherPage />} />
            <Route path="/play/shortFormQuiz/:code/student" element={<ShortFormQuizStudentPage />} />
            <Route path="/play/shortFormQuiz/:code/modify" element={<ShortFormQuizModifyPage />} />
            <Route path="/class-management" element={<ClassManagementPage />} />
            <Route path="/class-management/:classId" element={<ClassDetailPage />} />
            <Route path="/class-management/:classId/:studentId" element={<StudentDetailPage />} />
            <Route path="/class-management/:classId/activity/:activityId" element={<ActivityDetailPage />} />
            <Route path="/class-management/:classId/activity" element={<ActivityHistoryPage />} />
            <Route path="/class-management/:classId/school-record-history/:historyId" element={<SchoolRecordDetailPage />} />
            <Route path="/class-management/:classId/school-record-history" element={<SchoolRecordHistoryPage />} />
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="/help-center/terms-of-service" element={<TermsOfServicePage />} />
            <Route path="/help-center/privacy-policy" element={<PrivacyPolicyPage />} />
            <Route path="/help-center/faq" element={<FAQ />} />
            <Route path="/help-center/faq/:id" element={<FAQ />} />
            <Route path="/help-center/events/:id" element={<EventsPage />} />
            <Route path="/help-center/post/:id" element={<PostDetailPage />} />

            {/* fallback or redirect for old notice/updates URLs if necessary */}

            <Route path="/play/role-picker" element={<RolePickerHomePage />} />
            <Route path="/play/role-picker/:documentId" element={<RolePickerTeacherView />} />
            <Route path="/play/role-picker/:documentId/manual" element={<RolePickerTeacherView />} />
            <Route path="/play/role-picker/:documentId/teacher" element={<RolePickerTeacherView />} />
            <Route path="/play/role-picker/:documentId/student" element={<RolePickerStudentView />} />
            <Route path="/play/role-picker/:documentId/print" element={<RolePickerPrintPage />} />
            <Route path="/play/role-picker/:documentId/game" element={<RolePickerGamePage />} />
            <Route path="/play/marble-drawing" element={<MarbleDrawingHomePage />} />
            <Route path="/play/marble-drawing/class-setting" element={<MarbleDrawingClassSettingPage />} />
            <Route path="/play/marble-drawing/game" element={<MarbleDrawingSettingPage />} />
            <Route path="/play/marble-game" element={<MarbleGamePage />} />
            <Route path="/play/study-group" element={<StudyGroupPage />} />
            <Route path="/play/study-group/:groupId/student" element={<StudyGroupStudentPage />} />
            <Route path="/play/study-group/:groupId/question/:questionId" element={<SurveyQuestion />} />
            <Route path="/play/study-group/:groupId/result/:resultType" element={<SurveyResult />} />
            <Route path="/play/study-group/:groupId/print" element={<StudyGroupPrintPage />} />
            <Route path="/play/study-group/:groupId" element={<StudyGroupHomePage />} />
            <Route path="/test/:shortCode" element={<ShortUrlRedirect />} />
            <Route path="/play/teacher-test" element={<TeacherTestHomePage />} />
            <Route path="/play/teacher-test/paper" element={<TeacherTestPaperPage />} />
            <Route path="/play/teacher-test/student-paper" element={<TeacherTestStudentPaperPage />} />
            <Route path="/play/teacher-test/preview" element={<TeacherTestPreviewPage />} />
            <Route path="/play/teacher-test/dashboard" element={<TeacherTestDashboard />} />
            <Route path="/play/test-result" element={<TestResultPage />} />
            <Route path="/play/daily-saying" element={<DailySayingPage />} />
            <Route path="/play/daily-saying/upload" element={<SayingsUploadPage />} />
            <Route path="/play/daily-saying/excel-debugger" element={<ExcelDebugger />} />
            <Route path="/play/saying-paper" element={<SayingPaperPage />} />

            <Route path="/play/debate-battle" element={<DebateBattlePage />} />
            <Route path="/play/debate-battle/:roomCode" element={<DebateBattlePage />} />
            <Route path="/play/debate-battle/:roomCode/student" element={<DebateBattleStudentPage />} />

            <Route path="/play/debate-battle-english" element={<DebateBattleEnglishPage />} />
            <Route path="/play/debate-battle-english/:roomCode" element={<DebateBattleEnglishPage />} />
            <Route path="/play/debate-battle-english/:roomCode/student" element={<DebateBattleStudentPage />} />
            
            {/* 마인드맵 페이지 라우트 */}
            <Route path="/play/mind-map-solo" element={<MindMapSoloPage />} />
            <Route path="/play/mind-map-solo/teacher/:sessionId" element={<MindMapSoloPage />} />
            <Route path="/play/mind-map-solo/:sessionId" element={<MindMapStudentSoloPage />} />

            <Route path="/play/mind-map-together" element={<MindMapTogetherPage />} />
            <Route path="/play/mind-map-together/teacher/:sessionId" element={<MindMapTogetherPage />} />
            <Route path="/play/mind-map-together/:sessionId" element={<MindMapStudentTogetherPage />} />

            {/* 관리자 설정 페이지 - 관리자 전용 보호 */}
            <Route path="/admin-settings" element={
              <ProtectedRoute requireAdmin={true}>
                <AdminSettingMainPage />
              </ProtectedRoute>
            } />
            <Route path="/admin-settings/user-account" element={
              <ProtectedRoute requireAdmin={true}>
                <AdminSettingUserAccount />
              </ProtectedRoute>
            } />
            <Route path="/admin-settings/coupon" element={
              <ProtectedRoute requireAdmin={true}>
                <AdminSettingCoupon />
              </ProtectedRoute>
            } />
            <Route path="/admin-settings/notice-board" element={
              <ProtectedRoute requireAdmin={true}>
                <AdminSettingNoticeBoard />
              </ProtectedRoute>
            } />
            <Route path="/admin-settings/main-notice" element={
              <ProtectedRoute requireAdmin={true}>
                <AdminSettingMainNotice />
              </ProtectedRoute>
            } />
            
            {/* 생활기록부 생성 페이지 */}
            <Route path="/school-record" element={<SchoolRecordMainPage />} />

            {/* 짧은 URL 지원 (contents 없이) */}
            <Route path="/debate-battle" element={<DebateBattlePage />} />
            <Route path="/debate-battle/:roomCode" element={<DebateBattlePage />} />
            <Route path="/debate-battle/:roomCode/student" element={<DebateBattleStudentPage />} />
            
            {/* 콘텐츠 상세 페이지 - 다른 모든 /contents/* 라우트 다음에 catch-all로 위치 */}
            <Route path="/contents/:slug" element={<ContentDetailPage />} />
          </Routes>
        </Suspense>
      </div>
      {!hideHeaderFooterPages && !isPrivacyPage && !isTermsPage && (
        <MainFooter />
      )}

      {/* 전역 모달 렌더링 */}
      <LoginModal 
        isOpen={showLoginModal} 
        onClose={closeLoginModal} 
        onRequestRegister={openRegisterModal}
      />
      <RegisterModal 
        isOpen={showRegisterModal} 
        onClose={closeRegisterModal} 
        onRequestLogin={openLoginModal}
      />
    </div>
  );
}

export default App;
